import {Tooltip} from '@mui/material'
import {Box, Button, Modal, Typography} from '@viptech/react-components'
import {useSnackbar} from 'notistack'
import {useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {ValidateUserPermissions} from 'src/common/utils/ValidatePermissions'
import {BtnClose} from 'src/components/BtnClose'
import Dropdown from 'src/components/dropdown/Dropdown'
import {DropdownItem} from 'src/components/dropdown/DropdownProps'
import {Input} from 'src/components/input/Input'
import useCreateClient from 'src/hooks/api/client/useCreateClient'
import useUpdateClient from 'src/hooks/api/client/useUpdateClient'
import useUpdateDaysToRetainOnClient from 'src/hooks/api/client/useUpdateDaysToRetainOnClient'
import useCompanies from 'src/hooks/api/companies/useCompanies'
import api, {Client, CreateOrEditClient, DispatchTestEventResponse, EnumServer, Layout, Server} from 'src/services/api'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'
import {useWindowSize} from 'usehooks-ts'

type Props = {
  clientSelected?: Client
  isOpen: boolean
  closeModal: () => void
}

const RetentionDaysOptions = [
  {id: '0', label: '0'},
  {id: '1', label: '1'},
  {id: '3', label: '3'},
  {id: '7', label: '7'},
  {id: '10', label: '10'},
  {id: '30', label: '30'},
  {id: '35', label: '35'},
  {id: '180', label: '180'},
]

function ClientsModal(props: Props) {
  const {clientSelected, isOpen, closeModal} = props
  const navigate = useNavigate()
  const {enqueueSnackbar} = useSnackbar()

  const isCreate = clientSelected === undefined
  const [clientToAdd, setClientToAdd] = useState<CreateOrEditClient>({} as CreateOrEditClient)

  function shouldDisableButton() {
    const hasName = isCreate ? clientToAdd.clientName !== '' : clientSelected.name !== ''
    const hasCompanySelected = companySelected?.id !== undefined
    let hasClientInfo = false

    switch (serverType) {
      case EnumServer.SIGMA_CLOUD:
        hasClientInfo = !!clientToAdd.sigmaAccount || !!clientSelected?.sigmaAccount
        break
      case EnumServer.MONI:
        hasClientInfo = !!clientToAdd.moniClientInfo?.clientCode || !!clientSelected?.moniClientInfo?.clientCode
        break
      case EnumServer.ONE_PORTARIA:
        hasClientInfo = !!clientToAdd.onePortariaClientInfo?.clientCode || !!clientSelected?.onePortariaClientInfo?.clientCode
        break
      default:
        hasClientInfo = true
        break
    }

    const result = hasName && hasCompanySelected && hasClientInfo
    return !result
  }

  const [outputServers, setOutputServers] = useState<Server[]>([])
  const [serverClient, setServerClient] = useState<DropdownItem>()
  const [serverType, setServerType] = useState<number>()

  const [clientLayouts, setClientLayouts] = useState<Layout[]>([])
  const [layoutSelected, setLayoutSelected] = useState<DropdownItem>()

  const [companySelected, setCompanySelected] = useState<DropdownItem>()

  const hasPermissionViewDevices = ValidateUserPermissions('cameras:read')
  const hasReadLayoutsPermission = ValidateUserPermissions('layouts:read')
  const hasConfigAdvancedPermission = ValidateUserPermissions('camera-parameter-config:update')

  const [retainDaysEventsSelected, setRetainDaysEventsSelected] = useState<DropdownItem>()
  const [retainDaysImagensSelected, setRetainDaysImagensSelected] = useState<DropdownItem>()
  const [retainDaysStreamsSelected, setRetainDaysStreamsSelected] = useState<DropdownItem>()

  const handleDefaultDaysRetain = () => {
    if (isCreate && companySelected?.id) {
      const companyFind = companies?.pages.flatMap((it) => it.entities).find((item) => item.id === Number(companySelected.id))
      setRetainDaysEventsSelected({
        id: String(companyFind?.daysToRetainEvents ?? 0),
        label: `${String(companyFind?.daysToRetainEvents) ?? '0'} (default da empresa)`,
      })
      setRetainDaysImagensSelected({
        id: String(companyFind?.daysToRetainImagens ?? 0),
        label: `${String(companyFind?.daysToRetainImagens) ?? '0'} (default da empresa)`,
      })
      setRetainDaysStreamsSelected({
        id: String(companyFind?.daysToRetainStream ?? 0),
        label: `${String(companyFind?.daysToRetainStream) ?? '0'} (default da empresa)`,
      })
      return
    }
    if (clientSelected) {
      setRetainDaysEventsSelected({id: String(clientSelected.daysToRetainEvents), label: String(clientSelected.daysToRetainEvents)})
      setRetainDaysImagensSelected({id: String(clientSelected.daysToRetainImagens), label: String(clientSelected.daysToRetainImagens)})
      setRetainDaysStreamsSelected({id: String(clientSelected.daysToRetainStream), label: String(clientSelected.daysToRetainStream)})
      return
    }
    setRetainDaysEventsSelected(RetentionDaysOptions[0])
    setRetainDaysImagensSelected(RetentionDaysOptions[0])
    setRetainDaysStreamsSelected(RetentionDaysOptions[0])
  }

  useEffect(() => {
    handleDefaultDaysRetain()
  }, [isCreate, companySelected, clientSelected])

  const shouldEditRetainDays = () => {
    if (
      retainDaysEventsSelected?.id !== String(clientSelected?.daysToRetainEvents) ||
      retainDaysImagensSelected?.id !== String(clientSelected?.daysToRetainImagens) ||
      retainDaysStreamsSelected?.id !== String(clientSelected?.daysToRetainStream)
    )
      return true
    return false
  }

  const [testLoading, setTestLoading] = useState<boolean>(false)
  function handleTestEventResult({message, status}: DispatchTestEventResponse) {
    enqueueSnackbar(message, {variant: status === 200 ? 'success' : 'warning'})
  }

  async function dispatchTestEvent() {
    try {
      setTestLoading(true)
      let code: string | undefined | null = ''

      if (isCreate) {
        if (serverType === EnumServer.MONI) code = clientToAdd.moniClientInfo?.clientCode
        if (serverType === EnumServer.SIGMA_CLOUD) code = clientToAdd.sigmaAccount
        if (serverType === EnumServer.ONE_PORTARIA) code = clientToAdd.onePortariaClientInfo?.clientCode
      } else {
        if (serverType === EnumServer.MONI) code = clientSelected.moniClientInfo?.clientCode
        if (serverType === EnumServer.SIGMA_CLOUD) code = clientSelected.sigmaAccount
        if (serverType === EnumServer.ONE_PORTARIA) code = clientSelected.onePortariaClientInfo?.clientCode
      }

      if (!code) return enqueueSnackbar('Não foi possível gerar um evento', {variant: 'warning'})
      const companyId = clientToAdd.companyId ?? clientSelected?.companyId
      const response = await api.client.dispatchTestEvent({code, companyId: companyId ?? -1})
      handleTestEventResult(response.data.data)
    } catch (error) {
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Não foi possível gerar um evento', 'warning')
    } finally {
      setTestLoading(false)
    }
  }

  function disableTestButton() {
    if (isCreate) return !clientToAdd.moniClientInfo?.clientCode && !clientToAdd.sigmaAccount && !clientToAdd.onePortariaClientInfo?.clientCode
    return !clientSelected.moniClientInfo?.clientCode && !clientSelected.sigmaAccount && !clientSelected.onePortariaClientInfo?.clientCode
  }

  const [loadingServers, setLoadingServers] = useState(false)
  const getOutputServers = async (companyId: number) => {
    try {
      setLoadingServers(true)
      const response = await api.server.findManyPaged({
        page: 1,
        pageSize: 20,
        filter: {integrationType: 'SAÍDA', companyIds: [companyId]},
        includes: ['serverType'],
      })
      const servers = response.data.data.entities
      servers.unshift({id: -1, name: 'Sem Integração', ip: '', serverType: {id: -1}})
      setOutputServers(servers)
      return servers
    } catch (err) {
      return []
    } finally {
      setLoadingServers(false)
    }
  }

  useEffect(() => {
    async function getLayoutsByClient() {
      if (!hasReadLayoutsPermission) return
      if (!clientSelected) return

      const response = await api.layout.getMany({
        filter: {
          clientIds: [clientSelected.id],
        },
      })
      setClientLayouts(response.data.data.entities)
      if (!isCreate) {
        const layout = response.data.data.entities.find((it) => it.id === clientSelected.defaultLayoutId)
        if (layout) setLayoutSelected({id: String(layout.id), label: layout.name})
      }
    }
    getLayoutsByClient()
    getInitalServer()
  }, [clientSelected, isCreate])

  const getInitalServer = async () => {
    if (!isCreate && clientSelected) {
      const outputServers = await getOutputServers(clientSelected.companyId ?? -1)

      if (clientSelected.moniClientInfo) {
        setServerType(EnumServer.MONI)
        const server = outputServers.find((it) => it.serverTypeId === EnumServer.MONI)
        if (server) setServerClient({id: String(server.id), label: server.name})
      } else if (clientSelected.sigmaAccount) {
        setServerType(EnumServer.SIGMA_CLOUD)
        const server = outputServers.find((it) => it.serverTypeId === EnumServer.SIGMA_CLOUD)
        if (server) setServerClient({id: String(server.id), label: server.name})
      } else if (clientSelected.onePortariaClientInfo) {
        setServerType(EnumServer.ONE_PORTARIA)
        const server = outputServers.find((it) => it.serverTypeId === EnumServer.ONE_PORTARIA)
        if (server) setServerClient({id: String(server.id), label: server.name})
      } else {
        setServerType(undefined)
        setServerClient({id: '-1', label: 'Sem Integração'})
      }
    }
  }

  const {data: companies, isLoading: loadingCompany} = useCompanies()

  const {mutateAsync: createClient, isPending: loadingAddClient} = useCreateClient()
  const {mutateAsync: updateClient, isPending: loadingUpdateClient} = useUpdateClient()
  const {mutateAsync: updateDays} = useUpdateDaysToRetainOnClient()

  useEffect(() => {
    if (!isCreate && clientSelected) {
      const companyFind = companies?.pages.flatMap((it) => it.entities.find((item) => item.id === clientSelected.companyId) ?? [])
      if (companyFind?.length) {
        const companySelected = {id: String(companyFind[0].id), label: companyFind[0].name}
        setCompanySelected(companySelected)
      }
    }
  }, [companies, clientSelected])

  const handleCloseModal = () => {
    closeModal()
    setClientToAdd({} as CreateOrEditClient)
    setCompanySelected(undefined)
    navigate(`/registers/clients`)
  }

  const windowSize = useWindowSize()

  return (
    <Modal isOpen={isOpen} closeModal={() => handleCloseModal()} backgroundColor='white' width='600px'>
      <Box
        display='flex'
        padding='20px'
        direction='column'
        rowGap={isCreate ? '10px' : '20px'}
        justifyContent='space-between'
        style={{boxSizing: 'border-box'}}
        overflow='auto'
        height={windowSize.height <= 763 ? '500px' : undefined}>
        <Box display='flex' align='center'>
          <Typography variant='p' size='16px' children={isCreate ? 'Adicionar Cliente' : 'Editar Cliente'}></Typography>
          <BtnClose onClose={handleCloseModal} />
        </Box>

        <Box display='flex' direction='column' rowGap='15px'>
          <Input.Root
            label='Nome'
            labelFontSize='14px'
            borderColor='#D0D5DD'
            fontSize='12px'
            labelFontWeight='400'
            min={4}
            maxLength={99}
            required
            value={clientToAdd?.clientName}
            defaultValue={clientSelected?.name}
            onChange={(e) => setClientToAdd({...clientToAdd, clientName: e.target.value})}
          />

          <Input.Root
            label='E-mail'
            labelFontSize='14px'
            borderColor='#D0D5DD'
            fontSize='12px'
            labelFontWeight='400'
            min={4}
            maxLength={99}
            defaultValue={clientSelected?.email}
            value={clientToAdd.email ?? ''}
            onChange={(e) => setClientToAdd({...clientToAdd, email: e.target.value})}
          />

          <Dropdown
            label='Empresa'
            required
            fontSize='12px'
            borderColor='#D0D5DD'
            isLoading={loadingCompany}
            items={
              companies?.pages.flatMap((it) =>
                it.entities.map((company) => {
                  return {
                    id: String(company.id),
                    label: company.name,
                  }
                }),
              ) ?? []
            }
            selected={companySelected}
            onChangeSelected={(e) => {
              setCompanySelected(e)
              setClientToAdd({...clientToAdd, companyId: Number(e.id)})
              getOutputServers(Number(e.id))
            }}
            disabled={!isCreate}
            height={windowSize.height <= 763 ? '40px' : undefined}
          />

          {companySelected?.id && (
            <Dropdown
              label='Integração de Saída'
              selected={serverClient}
              fontSize='12px'
              borderColor='#D0D5DD'
              onChangeSelected={(e) => {
                setServerClient(e)
                setServerType(outputServers.find((server) => server.id === Number(e.id))?.serverTypeId)
                setClientToAdd({
                  ...clientToAdd,
                  moniClientInfo: undefined,
                  sigmaAccount: undefined,
                  onePortariaClientInfo: undefined,
                })
              }}
              isLoading={loadingServers}
              items={
                outputServers.map((it) => {
                  return {label: it.name, id: String(it.id)}
                }) ?? []
              }
              height={windowSize.height <= 763 ? '40px' : undefined}
              marginTop={windowSize.height <= 763 ? '12px' : undefined}
            />
          )}

          {serverType === EnumServer.MONI && (
            <Input.Root
              label='Código da Central de Alarme no Moni'
              labelFontSize='14px'
              borderColor='#D0D5DD'
              fontSize='12px'
              labelFontWeight='400'
              min={4}
              maxLength={99}
              required
              value={clientToAdd?.moniClientInfo?.clientCode ?? ''}
              defaultValue={clientSelected?.moniClientInfo?.clientCode}
              onChange={(e) =>
                setClientToAdd({
                  ...clientToAdd,
                  moniClientInfo: {clientCode: e.target.value, moniCompanyId: '1', sector: '99'},
                  sigmaAccount: undefined,
                  onePortariaClientInfo: undefined,
                })
              }
              marginTop={windowSize.height <= 763 ? '12px' : undefined}
            />
          )}
          {serverType === EnumServer.SIGMA_CLOUD && (
            <Input.Root
              label='Código da Central de Alarme no Sigma Cloud'
              labelFontSize='14px'
              borderColor='#D0D5DD'
              fontSize='12px'
              labelFontWeight='400'
              min={4}
              maxLength={99}
              required
              value={clientToAdd.sigmaAccount ?? ''}
              defaultValue={clientSelected?.sigmaAccount}
              onChange={(e) => setClientToAdd({...clientToAdd, sigmaAccount: e.target.value, moniClientInfo: undefined, onePortariaClientInfo: undefined})}
              marginTop={windowSize.height <= 763 ? '12px' : undefined}
            />
          )}
          {serverType === EnumServer.ONE_PORTARIA && (
            <Input.Root
              label='Código do cliente no sistema One'
              labelFontSize='14px'
              borderColor='#D0D5DD'
              fontSize='12px'
              labelFontWeight='400'
              min={4}
              maxLength={99}
              required
              value={clientToAdd.onePortariaClientInfo?.clientCode || ''}
              defaultValue={clientSelected?.onePortariaClientInfo?.clientCode}
              onChange={(e) =>
                setClientToAdd({...clientToAdd, onePortariaClientInfo: {clientCode: e.target.value}, sigmaAccount: undefined, moniClientInfo: undefined})
              }
              marginTop={windowSize.height <= 763 ? '12px' : undefined}
            />
          )}

          {!isCreate && clientLayouts.length > 0 && (
            <Dropdown
              label='Layout Padrão'
              fontSize='12px'
              borderColor='#D0D5DD'
              required
              selected={layoutSelected}
              onChangeSelected={(e) => {
                setLayoutSelected(e)
                setClientToAdd({...clientToAdd, defaultLayoutId: Number(e.id)})
              }}
              items={clientLayouts.map((layout) => {
                return {id: String(layout.id), label: layout.name}
              })}
              height={windowSize.height <= 763 ? '40px' : undefined}
              marginTop={windowSize.height <= 763 ? '12px' : undefined}
            />
          )}

          {hasConfigAdvancedPermission && (
            <Box width='100%' display='flex' direction='column' rowGap='20px' marginTop='10px'>
              <Box width='100%' display='flex' direction='column'>
                <Typography variant='span' size='16px' style={{fontWeight: 500}} children='Retenção de Imagens e Eventos' />
                <Typography variant='span' size='12px' color='#8E8E8E' children='Você pode alterar a configuração definida na empresa para este cliente.' />
                <Typography
                  variant='span'
                  size='12px'
                  color='#8E8E8E'
                  children='Personalize o período de retenção, em dias, para o armazenamento de eventos, imagens e vídeos.'
                />
              </Box>

              <Box display='flex' columnGap='8px'>
                <Dropdown
                  label='Eventos e Detecções'
                  required
                  fontSize='12px'
                  borderColor='#D0D5DD'
                  items={RetentionDaysOptions}
                  selected={retainDaysEventsSelected}
                  onChangeSelected={(e) => {
                    setRetainDaysEventsSelected(e)
                    setClientToAdd({...clientToAdd, daysToRetainEvents: Number(e.id)})
                  }}
                  height={windowSize.height <= 763 ? '40px' : undefined}
                />

                <Dropdown
                  label='Imagens'
                  required
                  fontSize='12px'
                  borderColor='#D0D5DD'
                  items={RetentionDaysOptions}
                  selected={retainDaysImagensSelected}
                  onChangeSelected={(e) => {
                    setRetainDaysImagensSelected(e)
                    setClientToAdd({...clientToAdd, daysToRetainImagens: Number(e.id)})
                  }}
                  height={windowSize.height <= 763 ? '40px' : undefined}
                />

                <Dropdown
                  label='Vídeos'
                  required
                  fontSize='12px'
                  borderColor='#D0D5DD'
                  items={RetentionDaysOptions}
                  selected={retainDaysStreamsSelected}
                  onChangeSelected={(e) => {
                    setRetainDaysStreamsSelected(e)
                    setClientToAdd({...clientToAdd, daysToRetainStream: Number(e.id)})
                  }}
                  height={windowSize.height <= 763 ? '40px' : undefined}
                />
              </Box>
            </Box>
          )}
          <Box width='100%' display='flex' align='center' justifyContent='space-between' marginTop='10px'>
            <Tooltip title={`Envie um evento de teste (1601) do cliente para sua Central de Alarme`}>
              <Button
                variant='contained'
                fontSize='12px'
                height='38px'
                width='150px'
                disabledTextColor='#8E8E8E'
                isLoading={testLoading}
                onClick={() => dispatchTestEvent()}
                disabled={disableTestButton()}>
                Evento de Teste
              </Button>
            </Tooltip>

            {!isCreate && hasPermissionViewDevices && (
              <Button
                variant='contained'
                fontSize='12px'
                height='38px'
                width='150px'
                disabledTextColor='#8E8E8E'
                onClick={() => navigate(`/registers/devices?client=${clientSelected?.id}`)}
                disabled={clientSelected?.cameras && clientSelected.cameras.length <= 0}>
                Listar Dispositivos
              </Button>
            )}
          </Box>
        </Box>

        <Box display='flex' width='100%' columnGap='10px' justifyContent='flex-end'>
          <Button fontSize='12px' height='38px' width='120px' variant='outlined' color='#8E8E8E' onClick={() => handleCloseModal()}>
            Cancelar
          </Button>
          <Button
            fontSize='12px'
            height='38px'
            width='120px'
            disabledTextColor='#8E8E8E'
            variant='contained'
            onClick={async () => {
              if (isCreate) await createClient(clientToAdd)
              else if (!isCreate && clientSelected) {
                await updateClient({clientId: clientSelected.id, body: clientToAdd})
                if (hasConfigAdvancedPermission && shouldEditRetainDays()) await updateDays({clientId: clientSelected.id, body: clientToAdd})
              }
              handleCloseModal()
            }}
            isLoading={loadingAddClient || loadingUpdateClient}
            disabled={shouldDisableButton()}>
            Salvar
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default ClientsModal
